/**
 * Merchant knowledge (TMMPDM-0112; tmm-api 0194 U2 and 0195 U1): what the product learns from
 * members' naming and banks, shared only by an admin's decision. Two tabs, one topic each (URL `tab`):
 * Merchant patterns (the approval queue) and Unlisted banks (the "my bank isn't listed" requests).
 * The tabs stay up when a tab's routes answer 404, so the other is still reachable. Switching tabs
 * clears the patterns' queue, search and page.
 *
 * Merchants are public facts, and people are private (MM-D-151): nothing on this page shows a member,
 * tenant or owner.
 */
import { Box } from '@mui/material';
import { useCopy } from '@/hooks/useCopy';
import { oneOf, text, useUrlState, type UrlSchema } from '@/hooks/useUrlState';
import { PageHeader } from '@/components/page';
import { FilterTabs } from '@/components/ui';
import { MerchantPatternsTab } from './MerchantPatternsTab';
import { UnlistedBanksTab } from './UnlistedBanksTab';
import { KNOWLEDGE_TABS, type KnowledgeTab } from './knowledge';

/** The keys each tab keeps in the URL: changing tabs clears the other tab's. */
const SHELL: UrlSchema<{ tab: KnowledgeTab; view: string; q: string; page: string }> = {
  tab: { default: 'patterns', parse: oneOf(KNOWLEDGE_TABS) },
  view: { default: '', parse: text },
  q: { default: '', parse: text },
  page: { default: '', parse: text },
};

export function MerchantKnowledgePage() {
  const { t } = useCopy();
  const [view, setView] = useUrlState(SHELL);
  return (
    <Box>
      <PageHeader
        title={t('page.merchantKnowledge.title', 'Merchant knowledge')}
        subtitle={t(
          'knowledge.subtitle',
          'Merchant names and banks learned from members, shared only when you approve them.',
        )}
      />
      <Box sx={{ mb: 2 }}>
        <FilterTabs
          ariaLabel={t('knowledge.tabsAria', 'Merchant knowledge sections')}
          active={view.tab}
          onChange={(key) => {
            const tab = oneOf(KNOWLEDGE_TABS)(key);
            if (tab && tab !== view.tab) setView({ tab, view: '', q: '', page: '' });
          }}
          tabs={[
            { key: 'patterns', label: t('knowledge.tab.patterns', 'Merchant patterns') },
            { key: 'banks', label: t('knowledge.tab.banks', 'Unlisted banks') },
          ]}
        />
      </Box>
      {view.tab === 'banks' ? <UnlistedBanksTab /> : <MerchantPatternsTab />}
    </Box>
  );
}
